import { Injectable } from '@angular/core';
import { IEmailCheckResponse } from '../modals/login-modal/login-modal.component';
import { BehaviorSubject } from 'rxjs';
import * as Parse from 'parse';
import { OrganizationService } from './organization.service';

interface ICurrentAgent {
    avatar: Parse.File | null;
    firstName?: string;
    firstname: string;
    lastName?: string;
    lastname: string;
    vestaNumber: string;
    phoneNumber: string;
    email: string;
}

export type VestaUserType = 'Admin' |  'AA' | 'SA' | 'AAA' | 'PM' | 'TC' | 'Vendor';

@Injectable()
export class AuthService {
    #currentUser: Parse.User | undefined;
    #userType: VestaUserType | undefined;
    #currentAgent: ICurrentAgent | undefined;
    #isUserLoggedIn: boolean = false;
    #isCustomerLoggedIn: boolean = false;
    #customerEmail: string | null = null;
    #customerId: string | null = null;
    #customerOrganizationId: string | null = null;
    #showAgentSection: boolean = false;

    public currentUserChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get currentUser() {
        return this.#currentUser as Parse.User;
    }

    set currentUser(user: Parse.User) {
        this.#currentUser = user;
    }

    get userType() {
        return this.#userType as VestaUserType;
    }

    set userType(type: VestaUserType) {
        this.#userType = type;
    }

    get currentAgent() {
        return this.#currentAgent as ICurrentAgent;
    }

    set currentAgent(agent: ICurrentAgent) {
        this.#currentAgent = agent;
    }

    get isUserLoggedIn() {
        return this.#isUserLoggedIn;
    }

    set isUserLoggedIn(value: boolean) {
        this.#isUserLoggedIn = value;
    }

    get isCustomerLoggedIn() {
        return this.#isCustomerLoggedIn;
    }

    set isCustomerLoggedIn(value: boolean) {
        this.#isCustomerLoggedIn = value;
    }

    get customerEmail() {
        return this.#customerEmail as string;
    }

    set customerEmail(email: string) {
        this.#customerEmail = email;
    }

    get customerId() {
        return this.#customerId as string;
    }

    set customerId(id: string) {
        this.#customerId = id;
    }

    get customerOrganizationId() {
        return this.#customerOrganizationId as string;
    }

    set customerOrganizationId(organizationId: string) {
        this.#customerOrganizationId = organizationId;
    }

    get showAgentSection() {
        return this.#showAgentSection;
    }

    set showAgentSection(value: boolean) {
        this.#showAgentSection = value;
    }

    get isAdmin() {
        return this.userType === 'Admin';
    }

    get isAA() {
        return this.userType === 'AA';
    }

    get isAAA() {
        return this.userType === 'AAA';
    }

    get isVendor() {
        return this.userType === 'Vendor';
    }

    get isSA() {
        return this.userType === 'SA';
    }

    get isPM() {
        return this.userType === 'PM';
    }

    get isTC() {
        return this.userType === 'TC';
    }

    constructor(private orgService: OrganizationService) {
        this.authenticate = this.authenticate.bind(this);
    }

    async logIn(username: string, password: string) {
        await Parse.User.logIn(username, password);
        this.currentUser = Parse.User.current() as Parse.User;
        return this.currentUser;
    }

    logOut() {
        return Parse.User.logOut();
    }

    setUserLastLogin() {
        this.currentUser.set('lastLogin', new Date());

        return this.currentUser.save();
    }

    removeCustomerAuthData() {
        localStorage.removeItem('customerEmail');
        localStorage.removeItem('customerUpdateToken');
        localStorage.removeItem('customerId');
        localStorage.removeItem('customerOrganizationId');
    }

    setCustomerAuthData(customerInfo: IEmailCheckResponse) {
        localStorage.setItem('customerEmail', customerInfo.customerEmail as string);
        localStorage.setItem('customerUpdateToken', customerInfo.updateToken as string);
        localStorage.setItem('customerId', customerInfo.id);
        localStorage.setItem('customerOrganizationId', customerInfo.organizationId as string);
    }

    setCustomerAuthDataFromObject(customer: Parse.Object) {
        localStorage.setItem('customerEmail', customer.get('email'));
        localStorage.setItem('customerUpdateToken', customer.get('updateToken'));
        localStorage.setItem('customerId', customer.id);
        localStorage.setItem('customerOrganizationId', customer.get('organization').id);
    }

    authenticate(): void {
        this.currentUser = Parse.User.current() as Parse.User;

        if (this.currentUser) {
            this.orgService.organization = this.currentUser.get('organization');
            this.userType = this.currentUser.get('type') || undefined;

            this.isUserLoggedIn = true;
            this.isCustomerLoggedIn = false;
            this.showAgentSection = false;
            this.removeCustomerAuthData();
        } else {
            this.isUserLoggedIn = false;

            if (localStorage.getItem('customerEmail') || localStorage.getItem('customerId')) {
                this.isCustomerLoggedIn = true;
                this.customerEmail = localStorage.getItem('customerEmail') as string;
                this.customerId = localStorage.getItem('customerId') as string;
                this.customerOrganizationId = localStorage.getItem('customerOrganizationId') as string;
            } else {
                this.isCustomerLoggedIn = false;
            }
        }

        void this.checkCurrentAgent();
    }

    async checkCurrentAgent() {
        if (this.isUserLoggedIn) {
            this.showAgentSection = false;
            return;
        }

        console.log('customer is logged in: %s', this.isCustomerLoggedIn);

        if (this.isCustomerLoggedIn) {
            this.currentAgent = await Parse.Cloud.run('getPreferredAgent', { customerId: this.customerId });

            if (this.currentAgent && this.currentAgent.firstname && this.currentAgent.lastname) {
                this.showAgentSection = true;
            }
            return;
        }

        const agentSlug = localStorage.getItem('agentSlug');
        if (agentSlug) {
            this.currentAgent = await Parse.Cloud.run('getAgentFromAgentSlug', {
                slug: agentSlug,
                organizationId: this.customerOrganizationId ? this.customerOrganizationId : this.orgService.organization.id
            });

            if (this.currentAgent) {
                this.showAgentSection = true;
            }
            return;
        }

        this.showAgentSection = false;
    }

    /**
     * Check whether a provided email is associated with an existing User or Customer class object
     *
     * @param email - the email address to be checked
     * @returns the following response object of type EmailCheckRespObj (defined in an interface above)
     *
     *  {
     *      isUser: boolean,
     *      isCustomer: boolean,
     *      id: string,
     *      updateToken: string
     *  }
     */
    checkExistingEmail(email: string) {
        return Parse.Cloud.run('checkAccountByEmail', {
            email: email.trim().toLowerCase(),
            organizationId: this.orgService.organization?.id
        });
    }
}
